import Color from 'color'
import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { setStyleProperties } from '../helpers/setStyleProperties'

/**
 * Organization state for multi-tenant operator support.
 *
 * V1 scope: stores the currently-selected organization.
 * V2 scope: will support switching between multiple orgs.
 */
export type Organization = {
  id: string
  name: string
  logo?: string
  primaryColor?: string
  requestPreWorkAuthorization?: boolean
}

type OrganizationContextValue = {
  /** The currently active organization (null until resolved). */
  organization: Organization | null
  /** Set the active organization after login. */
  setOrganization: (org: Organization) => void
  /** Clear organization on logout. */
  clearOrganization: () => void
}

const OrganizationContext = createContext<OrganizationContextValue>({
  organization: null,
  setOrganization: () => {},
  clearOrganization: () => {},
})

type OrganizationProviderProps = PropsWithChildren<{
  /** Org data from the CurrentUserProvider. */
  initialOrganization?: Organization | null
}>

/** Used when the organization has no brand color of its own — same as v1. */
const DEFAULT_PRIMARY_COLOR = '#252525'

const parseColorWithFallback = (
  color: string | undefined,
  fallback: string,
) => {
  try {
    return Color(color || fallback).rgb()
  } catch {
    return Color(fallback).rgb()
  }
}

export const OrganizationProvider = ({
  children,
  initialOrganization,
}: OrganizationProviderProps) => {
  // null = not set by user, undefined = explicitly cleared
  const [manualOrg, setManualOrg] = useState<Organization | null | undefined>(
    undefined,
  )

  // manualOrg takes priority, then initialOrganization from parent
  const organization =
    manualOrg === null
      ? null // explicitly cleared
      : (manualOrg ?? initialOrganization ?? null)

  const setOrganization = useCallback((org: Organization) => {
    setManualOrg(org)
  }, [])

  const clearOrganization = useCallback(() => {
    setManualOrg(null)
  }, [])

  // Paint the organization's brand color, mirroring v1's OrganizationProvider.
  //
  // Set on <html>, not <body> as v1 does: theme-v1.css aliases the design-system
  // tokens off this one (`--cs-color-primary: var(--ion-color-primary)`) at
  // :root, and a var() reference resolves against the element that declares it.
  // Overriding on <body> would leave those aliases stuck on the static default.
  const primaryColor = organization?.primaryColor
  useEffect(() => {
    const primary = parseColorWithFallback(primaryColor, DEFAULT_PRIMARY_COLOR)
    const tinted = primary.mix(Color('#ffffff'), 0.1)
    const shaded = primary.mix(Color('#000000'), 0.12)

    return setStyleProperties(document.documentElement, {
      '--ion-color-primary': primary.toString(),
      '--ion-color-primary-rgb': primary.array().map(Math.round).join(','),
      '--ion-color-primary-shade': shaded.toString(),
      '--ion-color-primary-tint': tinted.toString(),
      '--ion-color-primary-contrast':
        primary.luminosity() < 0.6 ? '#fff' : '#303030',
    })
  }, [primaryColor])

  const value = useMemo(
    () => ({ organization, setOrganization, clearOrganization }),
    [organization, setOrganization, clearOrganization],
  )

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  )
}

/**
 * Returns the current organization context.
 * Must be used within an `<OrganizationProvider>`.
 */
export const useOrganization = (): OrganizationContextValue =>
  useContext(OrganizationContext)
